import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { BackendCapabilities, VolumeSummary } from '@hub/shared';
import { api } from '../../../api/client';
import { ActionButton, Alert, Collapsible, EmptyRow, Loading, useFlash } from '../../../components/ui';
import {
  BulkBar, DOCKER_BASE, DataTable, Field, TabToolbar, enc, errMsg,
  makeBulkHandler, truncStyle, useSelection, useTabResource, type DockerCache,
} from './shared';

export function VolumesTab({ caps, cache, refreshToken }: {
  caps: BackendCapabilities;
  cache: DockerCache;
  refreshToken: number;
}) {
  const { data, loading, error, reload } = useTabResource<VolumeSummary[], { volumes: VolumeSummary[] }>({
    cache, tab: 'volumes', path: `${DOCKER_BASE}/volumes`, extract: (r) => r.volumes, refreshToken,
  });
  const { show, node: flashNode } = useFlash();
  const [formOpen, setFormOpen] = useState(false);

  const list = useMemo(() => data ?? [], [data]);
  const selectableIds = useMemo(
    () => list.filter((v) => !v.hubManaged && v.inUseBy.length === 0).map((v) => v.name),
    [list],
  );
  const sel = useSelection(selectableIds);

  const removeVolume = (name: string) => api.delete(`${DOCKER_BASE}/volumes/${enc(name)}`);

  const toolbar = (
    <TabToolbar onRefresh={() => { void reload(true); }} flash={flashNode}>
      <CreateVolumeForm open={formOpen} onToggle={() => setFormOpen((v) => !v)} onCreated={() => reload(true)} />
      {caps.prune?.volumes === true && (
        <ActionButton
          danger
          className="btn-ghost btn-sm"
          busyLabel="Pruning…"
          confirm="Remove unused anonymous volumes? Named volumes are kept."
          onClick={async () => {
            const r = await api.post<{ deleted: string[]; reclaimedBytes: number }>(
              `${DOCKER_BASE}/prune`, { target: 'volumes' },
            );
            show(`Removed ${r.deleted.length} volume(s)`, 'ok');
            await reload(true);
          }}
        >Prune unused</ActionButton>
      )}
    </TabToolbar>
  );

  if (loading && !data) return <>{toolbar}<Loading /></>;
  if (error && !data) return <>{toolbar}<Alert tone="error">{error}</Alert></>;

  return (
    <>
      {toolbar}
      <BulkBar count={sel.ids.length}>
        <ActionButton
          danger
          busyLabel="Deleting…"
          confirm={`Delete ${sel.ids.length} volume(s)? Data will be lost.`}
          onClick={makeBulkHandler(sel.ids, removeVolume, show, async () => { sel.clear(); await reload(true); })}
        >Delete</ActionButton>
      </BulkBar>

      <DataTable
        headers={[
          <input key="all" type="checkbox" checked={sel.allChecked} onChange={(e) => sel.selectAll(e.target.checked)} />,
          'Name', 'Driver', 'Mountpoint', 'Size', 'In use', 'Actions',
        ]}
      >
        {list.length === 0 && <EmptyRow colSpan={7}>No volumes.</EmptyRow>}
        {list.map((v) => {
          const inUse = v.inUseBy.length > 0;
          return (
            <tr key={v.name}>
              <td>
                {!v.hubManaged && !inUse && (
                  <input type="checkbox" checked={sel.has(v.name)} onChange={() => sel.toggle(v.name)} />
                )}
              </td>
              <td className="mono" style={{ color: 'var(--ha-text-primary)' }}>{v.name}</td>
              <td className="muted">{v.driver}</td>
              <td className="mono muted" style={truncStyle('18rem')}>{v.mountpoint || '–'}</td>
              <td className="muted">{v.sizeMb === null ? '–' : `${v.sizeMb} MB`}</td>
              <td className="muted">{inUse ? v.inUseBy.join(', ') : '–'}</td>
              <td>
                {v.hubManaged ? (
                  <Link className="pill pill-emerald" to="/admin" title="Belongs to a hub instance">Hub instance</Link>
                ) : inUse ? (
                  <button type="button" className="btn-ghost btn-sm" disabled title={`In use by ${v.inUseBy.join(', ')}`}>Delete</button>
                ) : (
                  <ActionButton
                    danger
                    busyLabel="Deleting…"
                    confirm={`Delete volume ${v.name}? Data will be lost.`}
                    onClick={async () => {
                      try {
                        await removeVolume(v.name);
                      } catch (err) {
                        show(errMsg(err), 'error');
                        throw err;
                      }
                      await reload(true);
                    }}
                  >Delete</ActionButton>
                )}
              </td>
            </tr>
          );
        })}
      </DataTable>
    </>
  );
}

function CreateVolumeForm({ open, onToggle, onCreated }: {
  open: boolean;
  onToggle: () => void;
  onCreated: () => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [driver, setDriver] = useState('');
  const { show, node } = useFlash();

  const submit = async () => {
    if (!name.trim()) {
      show('Name is required', 'error');
      throw new Error('Name is required');
    }
    const spec: { name: string; driver?: string } = { name: name.trim() };
    if (driver.trim()) spec.driver = driver.trim();
    try {
      await api.post(`${DOCKER_BASE}/volumes`, spec);
    } catch (err) {
      show(errMsg(err), 'error');
      throw err;
    }
    await onCreated();
  };

  return (
    <Collapsible label="＋ Create volume" open={open} onToggle={onToggle}>
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <Field label="Name"><input className="form-input" value={name} onChange={(e) => setName(e.target.value)} /></Field>
        <Field label="Driver"><input className="form-input" placeholder="local" value={driver} onChange={(e) => setDriver(e.target.value)} /></Field>
        <ActionButton className="btn-primary" busyLabel="Creating…" onClick={submit}>Create</ActionButton>
        {node}
      </div>
    </Collapsible>
  );
}
